import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ViewChild,
  input
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { Contacts } from "@capacitor-community/contacts";
import { ActionSheetController, IonicModule, ModalController, ToastController } from '@ionic/angular';
import { MaskitoModule } from "@maskito/angular";
import { MaskitoElementPredicateAsync, MaskitoOptions } from "@maskito/core";
import { BehaviorSubject } from "rxjs";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ReactiveFormsModule } from "@angular/forms";
import { StudentDto } from "../../interfaces/student_dto";
import { ColorService } from "src/app/shared/services/color.service";
import { ModeService } from "../../../utils/mode.service";
import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { StudentAddRequest } from "../../interfaces/student_add_request";
import { Store } from "@ngrx/store";
import { addStudent, updateStudent } from "../../ngrx/students.actions";
import { StudentUpdateRequest } from "../../interfaces/student_update_request";
import { AuthService } from "src/app/supabase/services/auth.service";
import { FunctionService } from "src/app/supabase/services/function.service";

@Component({
    selector: "app-new-student-dialog",
    templateUrl: "./new-student-dialog.component.html",
    styleUrls: ["./new-student-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonicModule,
        FormsModule,
        RouterModule,
        MaskitoModule,
        ReactiveFormsModule,
    ]
})
export class NewStudentDialogComponent {
  readonly student = input<StudentDto>(undefined);
  @ViewChild("fileUpload") fileUpload: any;

  profilePictureUrl = "";
  lfaAttachmentFile: File | null = null;

  studentForm: FormGroup;

  showImportContactButton?: boolean = true;

  public lfaActionSheetButtons = [
    {
      text: "Kamera",
      handler: () => {
        this.takeLfaPicture();
      },
    },
    {
      text: "Galerie",
      handler: () => {
        this.selectLfaPicture();
      },
    },
  ];

  constructor(
    public modeService: ModeService,
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private authService: AuthService,
    private colorService: ColorService,
    private actionSheetCtrl: ActionSheetController,
    private modalController: ModalController,
    private store: Store<any>,
    private toastController: ToastController,
    private functionService: FunctionService,
  ) {
    this.studentForm = this.fb.group({
      forename: ["", Validators.required],
      surname: [""],
      street: [""],
      postal_code: [""],
      city: [""],
      mobile_no: [""],
      mail_student: ["", Validators.email],
      birthday: [null],
      category: ["B", Validators.required],
      location: [""],
      exam_location: [""],
      vku_date: [null],
      lfa_number: [null],
      lfa_pin: [null],
      lfa_pid: [null],
      lfa_start: [null],
      lfa_end: [null],
      notes: [""],
      user_id: [""],
      has_profile_picture: [false],
      profile_picture_url: [null],
      profile_picture: [null],
      attachments: [null],
      new_attachments: [null],
      vku_completed: [false]
    });
  }

  ngOnInit() {
    if (this.student()) {
      this.studentForm.patchValue(this.student());
      if (this.student().vku_date && !this.student().vku_completed) {
        this.studentForm.patchValue({
          vku_completed: true
        });
      }
      this.showImportContactButton = false;
    }

    if (!Capacitor.isNativePlatform()) {
      this.showImportContactButton = false;
    }
  }

  readonly phoneMask: MaskitoOptions = {
    mask: [
      "+",
      /\d/,
      /\d/,
      " ",
      /\d/,
      /\d/,
      " ",
      /\d/,
      /\d/,
      /\d/,
      " ",
      /\d/,
      /\d/,
      " ",
      /\d/,
      /\d/,
    ],
  };

  readonly maskPredicate: MaskitoElementPredicateAsync = async (el) =>
    (el as HTMLIonInputElement).getInputElement();

  deviceContact$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  async addProfilePicture() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Aktionen",
      buttons: this.actionSheetButtons,
    });

    await actionSheet.present();
  }

  public actionSheetButtons = [
    {
      text: "Kamera",
      handler: () => {
        this.takePicture();
      },
    },
    {
      text: "Galerie",
      handler: () => {
        this.selectPicture();
      },
    },
  ];

  public async onFileChange(event) {

    let files = event.target.files;

    for (let i = 0; i < files.length; i++) {
      if (files[i]) {
        if (files[i].size > 10 * 1024 * 1024) {
          const toast = await this.toastController.create({
            message: "Die Datei ist zu gross (max. 10 MB)",
            duration: 1500,
            position: "bottom",
          });
    
          await toast.present();
          // Clear the file input
          event.target.value = '';
          this.fileUpload.nativeElement.value = '';

          // Reset the form control
          this.studentForm.patchValue({
            new_attachments: null
          });

          this.cdr.detectChanges();
          return;
        }
      }
    }

    if (files) {
      this.studentForm.patchValue({
        new_attachments: files
      });

      this.cdr.detectChanges();
    }
}

  async getDeviceContact() {
    const permissionState = await Contacts.requestPermissions();
    if (permissionState.contacts === "granted") {
      const result = await Contacts.pickContact({
        projection: {
          name: true,
          phones: true,
          postalAddresses: true,
          emails: true,
          birthday: true,
        },
      });
      return result;
    }
  }

  async takePicture() {

    const image = await Camera.getPhoto({
      quality: 40,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Camera,
    });

    

    this.studentForm.patchValue({
      profile_picture: image,
      has_profile_picture: true,
    });
    this.cdr.detectChanges();
  }

  async selectPicture() {
    const image = await Camera.getPhoto({
      quality: 40,
      allowEditing: false,
      resultType: CameraResultType.Uri,
      source: CameraSource.Photos,
    });

    this.studentForm.patchValue({
      profile_picture: image,
      has_profile_picture: true,
    });
    this.cdr.detectChanges();
  }

  cancel() {
    this.modalController.dismiss();
  }

  async save() {
    if (!this.studentForm.valid) {
      return;
    }

    let student = this.studentForm.value;
    student.user_id = await this.authService.getCurrentUserId();

    // remove profile_picture and attachments from student object
    const profile_picture = student.profile_picture;
    let attachments = student.new_attachments;
    
    // Add the LFA file to attachments if it exists
    if (this.lfaAttachmentFile) {
      if (attachments) {
        // Convert FileList to array and add the LFA file
        attachments = Array.from(attachments);
        attachments.push(this.lfaAttachmentFile);
      } else {
        // Create a new array with just the LFA file
        attachments = [this.lfaAttachmentFile];
      }
    }

    delete student.profile_picture;
    delete student.profile_picture_url;
    delete student.new_attachments;

    // Handle empty strings for dates
    if (student.birthday === "") {
      student.birthday = null;
    }
    if (student.vku_date === "") {
      student.vku_date = null;
    }
    
    // If VKU is not completed, ensure the date is null
    if (!student.vku_completed) {
      student.vku_date = null;
    }
    
    if (student.lfa_start === "") {
      student.lfa_start = null;
    }
    if (student.lfa_end === "") {
      student.lfa_end = null;
    }

    const studentValue = this.student();
    if (studentValue) {
      student.student_id = studentValue.student_id;
      student = student as StudentUpdateRequest;

      this.store.dispatch(
        updateStudent({
          student: student,
          profile_picture: profile_picture,
          attachments: attachments !== null ? Array.from(attachments) : null,
        })
      );
    } else {
      student = student as StudentAddRequest;
      this.store.dispatch(
        addStudent( {
          student: student,
          profile_picture: profile_picture,
          attachments: attachments !== null ? Array.from(attachments) : null,
        })
      )
    }

    this.modalController.dismiss();
  }

  async importContact() {
    this.getDeviceContact().then((result) => {
      if (result) {
        const contact = result.contact;

        const { day, month, year } = contact.birthday || {
          day: null,
          month: null,
          year: null,
        };

        if (day && month && year) {
          var birthdayDate = new Date(year, month - 1, day);
        }

        const mobile_no =
          (contact.phones && contact.phones[0] && contact.phones[0].number) ||
          "";
        // format mobile_no to be compatible with mask -> e.g. 0793606549 -> +41 79 360 65 49
        // always replace 07 in the beginning with +41 7
        // always replace 00xx in the beginning with +xx
        // format the rest with spaces -> +41 79 360 65 49
        let mobile_no_formatted = mobile_no
          .replace(/^07/, "+41 7")
          .replace(/^00/, "+");
        // first replace all spaces with empty string
        mobile_no_formatted = mobile_no_formatted.replace(/\s/g, "");
        // now format the rest with spaces
        mobile_no_formatted = mobile_no_formatted.replace(
          /(\d{2})(\d{2})(\d{3})(\d{2})(\d{2})/,
          "$1 $2 $3 $4 $5"
        );

        this.studentForm.patchValue({
          forename: contact.name.given || "",
          surname: contact.name.family || "",
          street:
            (contact.postalAddresses &&
              contact.postalAddresses[0] &&
              contact.postalAddresses[0].street) ||
            "",
          postal_code:
            (contact.postalAddresses &&
              contact.postalAddresses[0] &&
              contact.postalAddresses[0].postcode) ||
            "",
          city:
            (contact.postalAddresses &&
              contact.postalAddresses[0] &&
              contact.postalAddresses[0].city) ||
            "",
          mobile_no: mobile_no_formatted,
          mail_student:
            (contact.emails &&
              contact.emails[0] &&
              contact.emails[0].address) ||
            "",
          birthday: birthdayDate || null,
        });
      }
      this.deviceContact$.next(result);

      this.studentForm.controls["mail_student"].markAsUntouched();
      this.cdr.detectChanges();
    });
  }

  getColor(student: StudentDto) {
    return this.colorService.getColor(student.forename);
  }

  async scanLfa() {
    const actionSheet = await this.actionSheetCtrl.create({
      header: "Lernfahrausweis scannen",
      buttons: this.lfaActionSheetButtons,
    });

    await actionSheet.present();
  }

  async takeLfaPicture() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
      });
      
      await this.processLfaImage(image);
    } catch (error) {
      console.error('Error taking LFA picture:', error);
    }
  }

  async selectLfaPicture() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
      });
      
      await this.processLfaImage(image);
    } catch (error) {
      console.error('Error selecting LFA picture:', error);
    }
  }

  async processLfaImage(cameraFile: any) {
    try {
      const blob = await fetch(cameraFile.webPath).then(r => r.blob());
      const file = new File([blob], `Lernfahrausweis.${cameraFile.format}`, { type: `image/${cameraFile.format}` });
      
      // Call the function service to extract data
      const lfaData = await this.functionService.getLfaData([file], {});
      
      if (lfaData) {
        // Update form with extracted data
        this.studentForm.patchValue(lfaData);
        
        // Store the LFA image for attachment
        this.lfaAttachmentFile = file;
        
        // Show confirmation toast
        const toast = await this.toastController.create({
          message: "Lernfahrausweis wird als Anhang hinzugefügt",
          duration: 1500,
          position: "bottom",
          color: "success"
        });
        toast.present();
        
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error processing LFA image:', error);
      const toast = await this.toastController.create({
        message: "Fehler beim Scannen des Lernfahrausweises",
        duration: 2000,
        position: "bottom",
        color: "danger"
      });
      toast.present();
    }
  }

  async copyAddressToMeetingPoint() {
    const street = this.studentForm.get('street').value;
    const postalCode = this.studentForm.get('postal_code').value;
    const city = this.studentForm.get('city').value;
    
    if (!street && !postalCode && !city) {
      const toast = await this.toastController.create({
        message: "Keine Adressdaten vorhanden",
        duration: 2000,
        position: "bottom",
        color: "warning"
      });
      toast.present();
      return;
    }
    
    // Format the address as "Street, Postal Code City"
    let location = '';
    if (street) location += street;
    if (postalCode || city) {
      if (location) location += ', ';
      if (postalCode) location += postalCode;
      if (city) {
        if (postalCode) location += ' ';
        location += city;
      }
    }
    
    this.studentForm.patchValue({
      location: location
    });
    
    const toast = await this.toastController.create({
      message: "Adresse als Treffpunkt übernommen",
      duration: 1500,
      position: "bottom",
      color: "success"
    });
    toast.present();
    
    this.cdr.detectChanges();
  }

  toggleVkuCompleted() {
    const currentValue = this.studentForm.get('vku_completed').value;
    this.studentForm.patchValue({
      vku_completed: !currentValue
    });
    
    if (currentValue) {
      this.studentForm.patchValue({
        vku_date: null
      });
    }
    
    this.cdr.detectChanges();
  }
}
